import { Component, EventEmitter, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { combineLatest } from 'rxjs';
import { PermissionService } from '../../../core/services/permission/permission.service';
import { TaxService } from '../../../data/logic/tax.service';
import { PurchaseOrder, PurchaseOrderInvoice } from '../../../data/models';
import { Integration } from '../../../data/models/integrations.model';
import { RepoIntegrationsService } from '../../../data/repositories/repo-integrations.service';
import { RepoJobsService } from '../../../data/repositories/repo-jobs.service';
import { SharedDataService } from '../../../data/services/shared-data.service';

@Component({
    selector: 'app-purchase-order-invoice',
    templateUrl: './purchase-order-invoice.component.html',
    standalone: false,
})
export class PurchaseOrderInvoiceComponent implements OnInit {
    dateFormat = 'DD/MM/YYYY';
    // @ts-ignore TS2564
    invoice: PurchaseOrderInvoice;
    // @ts-ignore TS2564
    purchaseOrder: PurchaseOrder;
    readonly = false;
    taxCode = '';
    parentTasks: any[] = [];
    isAllowedToReceipt = false;
    items: any[] = [];
    hasNewItems = false;
    referenceIsDuplicate = false;
    action = new EventEmitter();
    readonly syncBlocked: string = 'blocked';
    accountSyncValid = false;
    isBillBeingImportedIntoPurchaseOrder = false;

    constructor(
        private bsModalRef: BsModalRef,
        private translateService: TranslateService,
        public taxService: TaxService,
        private permissionService: PermissionService,
        private sharedData: SharedDataService,
        private repoJobs: RepoJobsService,
        private readonly integrationsService: RepoIntegrationsService
    ) {
        this.translateService.get('general.dateFormat').subscribe((res: any) => {
            this.dateFormat = res;
        });
    }

    ngOnInit(): void {
        this.purchaseOrder = this.invoice.PurchaseOrder;
        this.taxCode =
            this.purchaseOrder.Job.CalculationMethod === 0 ? this.sharedData.UserData.tenant.TaxCode : this.purchaseOrder.TaxRatesShortCode;

        this.readonly =
            !this.isBillBeingImportedIntoPurchaseOrder &&
            (this.purchaseOrder.OrderStatus === 'Received' ||
                this.purchaseOrder.OrderStatus === 'Cancelled' ||
                !!this.purchaseOrder.ID_JobPayment);
        this.parentTasks = this.getParentTasks();

        combineLatest([
            this.permissionService.getState('jobs.maintain-purchase-orders.receipt-purchase-order'),
            this.permissionService.getState('jobs.maintain-work-orders.receipt-work-order'),
        ]).subscribe((val) => {
            this.isAllowedToReceipt =
                (this.purchaseOrder.OrderType === 'purchase' && val[0]) || (this.purchaseOrder.OrderType === 'work' && val[1]);
        });

        this.load();
        // If a new invoice then prepopulate values
        if (this.invoice.entityAspect.entityState.isAdded()) this.prepopulate();
        this.invoice.calculateTotal();
        this.integrationsService.GetConnectedAccountingProvider().then((integration: Integration | null) => {
            this.accountSyncValid = !!integration;
        });
    }

    load() {
        const items = this.isBillBeingImportedIntoPurchaseOrder
            ? this.invoice.PurchaseOrderInvoiceItems
            : this.purchaseOrder.PurchaseOrderItems;

        this.items = items
            .map((i) => {
                if (!i.IsDeleted) {
                    let invoiceItem = this.invoice.PurchaseOrderInvoiceItems.find((x) => {
                        return x.ID_PurchaseOrderItem === i.ID_PurchaseOrderItem;
                    });
                    const isNew = !invoiceItem;
                    if (!invoiceItem) {
                        invoiceItem = this.repoJobs.CreatePurchaseOrderInvoiceItem(this.invoice, {
                            ID_PurchaseOrderItem: i.ID_PurchaseOrderItem,
                            UnitCost: i.UnitCost,
                            TaxRates: i.TaxRates,
                            CostItemType: i.CostItemType,
                        });

                        // @ts-ignore TS2532
                        invoiceItem.TaxRates = i.TaxRates;
                    }

                    const orderItem = this.isBillBeingImportedIntoPurchaseOrder
                        ? this.purchaseOrder.PurchaseOrderItems.find((poi) => poi.ID_PurchaseOrderItem == invoiceItem?.ID_PurchaseOrderItem)
                        : i;

                    return {
                        orderItem: orderItem,
                        invoiceItem: invoiceItem,
                        isNew,
                    };
                }

                return null;
            })
            .filter((item) => !!item)
            .sort((a, b) => {
                return a!.orderItem!.Order - b!.orderItem!.Order;
            });
    }

    prepopulate() {
        if (!this.items || !this.items.length) return;

        // @ts-ignore TS7034
        let allReceivedItems = [];
        const allOtherInvoices = this.purchaseOrder.PurchaseOrderInvoices.filter((i) => {
            return i.ID_PurchaseOrderInvoice !== this.invoice.ID_PurchaseOrderInvoice && !i.IsDeleted;
        });
        allOtherInvoices.forEach((i) => {
            // @ts-ignore TS7005
            allReceivedItems = allReceivedItems.concat(i.PurchaseOrderInvoiceItems);
        });

        this.items.forEach((i) => {
            const qtyOrdered = i.orderItem.Quantity;
            // filter received items to the same order item ID, then sum the total
            // @ts-ignore TS7005
            const qtyReceived = allReceivedItems
                .filter((r) => {
                    return r.ID_PurchaseOrderItem === i.orderItem.ID_PurchaseOrderItem;
                })
                .reduce((total, val) => {
                    return total + val.Quantity;
                }, 0);
            if (qtyReceived > qtyOrdered) i.invoiceItem.Quantity = 0;
            else i.invoiceItem.Quantity = qtyOrdered - qtyReceived;
        });
        // Populate delivery cost if not already populated on another invoice
        if (
            !allOtherInvoices.find((i) => {
                return i.DeliveryCost !== 0;
            })
        ) {
            this.invoice.DeliveryCost = this.purchaseOrder.DeliveryCost;
            this.invoice.DeliveryTaxRates = this.purchaseOrder.DeliveryTaxRates;
        }
    }

    addItem() {
        // record that there are new items
        this.hasNewItems = true;

        const order = this.invoice.PurchaseOrder.PurchaseOrderItems.length
            ? // @ts-ignore TS2532
              _.sortBy(this.invoice.PurchaseOrder.PurchaseOrderItems, 'Order').reverse()[0].Order + 1
            : 0;

        const newOrderItem = this.repoJobs.CreatePurchaseOrderItem(this.purchaseOrder, {
            Quantity: 0,
            Order: order,
        });

        const newInvoiceItem = this.repoJobs.CreatePurchaseOrderInvoiceItem(this.invoice, {
            ID_PurchaseOrderItem: newOrderItem.ID_PurchaseOrderItem,
            UnitCost: 0,
        });

        const newItem = {
            isNew: true,
            orderItem: newOrderItem,
            invoiceItem: newInvoiceItem,
        };

        this.items.push(newItem);
        this.invoice.calculateTotal();
    }

    // @ts-ignore TS7006
    deleteItem(item) {
        if (!item.isNew) return;
        const index = this.items.indexOf(item);
        this.items.splice(index, 1);
        item.orderItem.entityAspect.rejectChanges();
        item.invoiceItem.entityAspect.rejectChanges();
        this.invoice.calculateTotal();
    }

    deleteAllNewItems() {
        for (let i = this.items.length - 1; i >= 0; i--) {
            const item = this.items[i];
            if (item.isNew) {
                item.orderItem.entityAspect.rejectChanges();
                item.invoiceItem.entityAspect.rejectChanges();
                this.items.splice(i, 1);
            }
        }
        this.invoice.calculateTotal();
    }

    onReferenceChanged() {
        this.checkReferenceIsDuplicate();
    }

    checkReferenceIsDuplicate() {
        this.referenceIsDuplicate = false;
        if (this.invoice.Reference) {
            this.repoJobs
                .CheckPurchaseOrderSupplierInvoiceRefExists(this.invoice.Reference, this.invoice.ID_PurchaseOrderInvoice)
                .then((response) => {
                    if (response.results && response.results[0]) this.referenceIsDuplicate = true;
                });
        }
    }

    getParentTasks() {
        // @ts-ignore TS2532
        const filtered = this.invoice.PurchaseOrder.Job.JobItems.filter((i) => {
            return !i.IsDeleted && i.IsParent && i.Description;
        });
        const ordered = _.orderBy(filtered, ['OrderNumber']);
        const map = _.map(ordered, (t) => {
            return t.Description;
        });
        return _.uniq(map);
    }

    incrementAdjustment(val: number) {
        this.invoice.Adjustment += val;
    }

    // @ts-ignore TS7006
    costItemTypeChanged(item) {
        item.orderItem.CostItemType = item.invoiceItem.CostItemType;
        item.orderItem.TaxRates = item.invoiceItem.TaxRates;
    }

    // @ts-ignore TS7006
    taxRatesChanged(evt, item) {
        item.invoiceItem.TaxRates = evt;
        item.orderItem.TaxRates = evt;
    }

    getInvoiceReference() {
        return encodeURIComponent(this.invoice.Reference);
    }

    ok(markAsReceived?: boolean) {
        if (markAsReceived) {
            this.purchaseOrder.OrderStatus = 'Received';
        }

        this.bsModalRef.hide();

        if (!this.isBillBeingImportedIntoPurchaseOrder) {
            this.action.emit({ cancelled: false });
        }
    }

    cancel() {
        this.deleteAllNewItems();
        this.bsModalRef.hide();
        this.action.emit({ cancelled: true });
    }

    clearSyncData() {
        this.invoice.AccSyncData = null;
        this.bsModalRef.hide();
        this.action.emit({ cancelled: false });
    }

    blockAccountingSync() {
        this.invoice.AccSyncData = 'blocked';
        this.bsModalRef.hide();
        this.action.emit({ cancelled: false });
    }
}
